import React from 'react';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import { Box } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from '../Button/types';
import { FacebookPhotoUploadProps } from './types';
import { useUploadFacebookPhotos } from 'src/api/hooks/useFacebookPost';
import { useFacebookPosts } from 'src/hooks/useFacebookPosts';
import { validateFacebookImage } from 'src/utils/facebookPostUtils';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';

export const FacebookPhotoUpload: React.FC<FacebookPhotoUploadProps> = ({
  setFacebookImages,
  facebookImages,
  setIsPhotoUploading,
  isPhotoUploading,
  isLoading,
  index,
  setFbPosts,
  postId,
  setPostSchedule,
}) => {
  const { t } = useTranslation();

  const { updateFacebookPost } = useFacebookPosts();

  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const handleFacebookPhotoUpload = (imageList: ImageListType, selectedImage: File) => {
    setFacebookImages(prev => prev.map((images, i) => (i === index ? imageList : images)));
    setIsPhotoUploading(prev => prev.map((value, i) => (i === index ? true : value)));

    const formData = new FormData();
    formData.append('file', selectedImage);

    uploadFacebookPhoto({ variables: formData });
  };

  const onChange = (imageList: ImageListType) => {
    if (isPhotoUploading[index] || !imageList?.[0]?.file) return;

    const selectedImage = imageList[0].file;

    const validationResult = validateFacebookImage(selectedImage, t);

    if (!validationResult.valid) {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: validationResult.message,
      });
      return;
    }

    handleFacebookPhotoUpload(imageList, selectedImage);
  };

  const [uploadFacebookPhoto] = useUploadFacebookPhotos({
    onComplete: res => {
      if (!res?.uploadedImageUrl.length) return;

      setFbPosts(prev =>
        prev.map((post, i) =>
          i === index ? { ...post, postPictureUrl: res.uploadedImageUrl } : post,
        ),
      );

      updateFacebookPost({
        variables: {
          updatedFacebookPost: {
            id: postId,
            postPictureUrl: res.uploadedImageUrl,
          },
        },
      });

      setPostSchedule(prev => ({
        ...prev,
        facebookPosts: prev.facebookPosts.map((post, i) =>
          i === index ? { ...post, postPictureUrl: res.uploadedImageUrl } : post,
        ),
      }));

      setIsPhotoUploading(prev => prev.map((value, i) => (i === index ? false : value)));
    },
    onError: () => {
      setIsPhotoUploading(prev => prev.map((value, i) => (i === index ? false : value)));
    },
  });

  return (
    <ImageUploading
      acceptType={['jpg', 'jpeg', 'png']}
      value={facebookImages}
      onChange={onChange}
      maxNumber={1}>
      {({ onImageUpload }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Button
            sx={{
              gap: 1,
            }}
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            disabled={isLoading}
            type="button"
            onClick={onImageUpload}>
            <UploadIcon fontSize="small" />
            <Trans>photoUpload.uploadButton</Trans>
          </Button>
        </Box>
      )}
    </ImageUploading>
  );
};
